import {
  Shield,
  Camera,
  Users,
  AlertTriangle,
  Smartphone,
  Eye,
  Thermometer,
  Zap,
  Activity,
  Settings,
  Building,
  Cpu,
  BarChart3,
  Wrench,
  Battery,
  Globe,
  TrendingUp,
  Clock,
  CheckCircle,
  Lock,
  Database,
} from 'lucide-react';
import { ProductConfigs } from './types';

export const facilitiesSolutionsConfigs: ProductConfigs = {
  'security-surveillance-management': {
    keyBenefits: [
      {
        icon: Camera,
        title: 'AI-Powered Video Surveillance Network',
        description: 'Advanced video analytics with real-time threat detection, facial recognition, and automated incident response across multiple locations',
        metrics: '99.9% uptime with 4K resolution monitoring',
        color: 'from-blue-500 to-cyan-400',
      },
      {
        icon: Shield,
        title: 'Integrated Access Control Management',
        description: 'Unified access control system with biometric authentication, visitor management, and real-time entry monitoring',
        metrics: 'Secure access for 10,000+ users simultaneously',
        color: 'from-green-500 to-emerald-400',
      },
      {
        icon: AlertTriangle,
        title: 'Intelligent Incident Management Workflow',
        description: 'Automated incident detection, classification, and response coordination with emergency services integration',
        metrics: '80% faster incident response times',
        color: 'from-orange-500 to-amber-400',
      },
      {
        icon: Eye,
        title: 'Real-Time Threat Detection & Analysis',
        description: 'Machine learning algorithms for behavioral analysis, perimeter monitoring, and predictive threat assessment',
        metrics: '95% threat detection accuracy',
        color: 'from-purple-500 to-violet-400',
      },
      {
        icon: Users,
        title: 'Comprehensive Visitor Management System',
        description: 'Digital check-in, badge printing, host notifications, and visitor tracking with compliance reporting',
        metrics: 'Process 1,000+ visitors daily with ease',
        color: 'from-indigo-500 to-blue-400',
      },
      {
        icon: Smartphone,
        title: 'Mobile Security Monitoring Platform',
        description: 'Real-time mobile access to surveillance feeds, alerts, and security controls from anywhere',
        metrics: '24/7 remote monitoring capabilities',
        color: 'from-teal-500 to-cyan-400',
      },
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: 'AI-Powered Behavioral Analytics Engine',
        description: 'Advanced machine learning for crowd behavior analysis, anomaly detection, and predictive security insights',
        metrics: 'Analyze 1M+ behavioral patterns daily',
        color: 'from-rose-500 to-pink-400',
      },
      {
        icon: Eye,
        title: 'Advanced Facial Recognition System',
        description: 'Multi-biometric authentication with liveness detection, anti-spoofing, and real-time identity verification',
        metrics: '99.8% facial recognition accuracy',
        color: 'from-emerald-500 to-teal-400',
      },
      {
        icon: TrendingUp,
        title: 'Predictive Security Analytics Platform',
        description: 'Historical data analysis, pattern recognition, and predictive modeling for proactive security measures',
        metrics: 'Predict security incidents 72 hours in advance',
        color: 'from-blue-500 to-indigo-400',
      },
      {
        icon: Zap,
        title: 'Automated Alert & Response Routing',
        description: 'Smart alert prioritization, automated escalation workflows, and integration with emergency response systems',
        metrics: '50% reduction in false alarms',
        color: 'from-yellow-500 to-orange-400',
      },
      {
        icon: Settings,
        title: 'Enterprise Integration APIs',
        description: 'Seamless integration with existing security systems, HR platforms, and facility management solutions',
        metrics: 'Connect with 100+ enterprise systems',
        color: 'from-purple-500 to-pink-400',
      },
      {
        icon: Database,
        title: 'Cloud-Based Evidence Management',
        description: 'Secure evidence storage, chain of custody tracking, and automated compliance reporting',
        metrics: 'Store 10TB+ of secure video evidence',
        color: 'from-cyan-500 to-blue-400',
      },
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated reporting and continuous monitoring',
        metrics: '100% compliance audit success rate',
        color: 'from-green-500 to-emerald-400',
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis, security posture optimization, and comprehensive risk assessment',
        metrics: '360° security visibility dashboard',
        color: 'from-blue-500 to-cyan-400',
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited facilities, users, and concurrent connections',
        metrics: 'Scale to 10,000+ locations seamlessly',
        color: 'from-purple-500 to-violet-400',
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation, real-time attack prevention, and automated security responses',
        metrics: '99.99% attack mitigation success',
        color: 'from-orange-500 to-red-400',
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, smart card integration, and passwordless access',
        metrics: 'Support for 20+ authentication methods',
        color: 'from-emerald-500 to-teal-400',
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM capabilities, behavioral analytics, and automated incident response',
        metrics: 'Process 1M+ security events per second',
        color: 'from-indigo-500 to-blue-400',
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, secure backup systems, and comprehensive recovery solutions',
        metrics: '99.999% data recovery guarantee',
        color: 'from-violet-500 to-purple-400',
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation, anomaly detection, and continuous network monitoring',
        metrics: 'Monitor 100,000+ network endpoints',
        color: 'from-cyan-500 to-teal-400',
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, compliance validation, and security posture improvement',
        metrics: '24/7 automated security assessments',
        color: 'from-rose-500 to-pink-400',
      },
    ],
  },

  'hvac-control-monitoring': {
    keyBenefits: [
      {
        icon: Thermometer,
        title: 'Intelligent Climate Control System',
        description: 'AI-driven temperature and humidity management with zone-based control and occupancy sensing',
        metrics: 'Maintain ±1°F temperature accuracy',
        color: 'from-blue-500 to-cyan-400',
      },
      {
        icon: Battery,
        title: 'Advanced Energy Optimization Algorithms',
        description: 'Machine learning-powered energy efficiency optimization reducing consumption while maintaining comfort',
        metrics: '35% reduction in energy costs',
        color: 'from-green-500 to-emerald-400',
      },
      {
        icon: AlertTriangle,
        title: 'Predictive Maintenance Alert System',
        description: 'Proactive equipment monitoring with failure prediction and automated maintenance scheduling',
        metrics: '90% reduction in equipment downtime',
        color: 'from-orange-500 to-amber-400',
      },
      {
        icon: BarChart3,
        title: 'Real-Time Performance Dashboard',
        description: 'Comprehensive monitoring interface with energy consumption tracking and system performance analytics',
        metrics: 'Monitor 1,000+ HVAC units simultaneously',
        color: 'from-purple-500 to-violet-400',
      },
      {
        icon: Activity,
        title: 'Environmental Sensor Integration',
        description: 'Advanced IoT sensor network for air quality, occupancy, and environmental condition monitoring',
        metrics: 'Track 50+ environmental parameters',
        color: 'from-indigo-500 to-blue-400',
      },
      {
        icon: Clock,
        title: 'Automated Scheduling & Control System',
        description: 'Smart scheduling based on occupancy patterns, weather forecasts, and energy pricing',
        metrics: 'Optimize schedules for 24/7 efficiency',
        color: 'from-teal-500 to-cyan-400',
      },
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: 'AI-Driven Climate Optimization Engine',
        description: 'Machine learning algorithms for predictive climate control, energy optimization, and comfort maximization',
        metrics: 'Process 10M+ data points per hour',
        color: 'from-rose-500 to-pink-400',
      },
      {
        icon: Wrench,
        title: 'Predictive Equipment Failure Detection',
        description: 'Advanced analytics for equipment health monitoring, failure prediction, and maintenance optimization',
        metrics: 'Predict failures 30 days in advance',
        color: 'from-emerald-500 to-teal-400',
      },
      {
        icon: Smartphone,
        title: 'Mobile Technician Application',
        description: 'Field service app with augmented reality diagnostics, work order management, and real-time communication',
        metrics: 'Increase technician efficiency by 40%',
        color: 'from-blue-500 to-indigo-400',
      },
      {
        icon: TrendingUp,
        title: 'Energy Consumption Analytics Platform',
        description: 'Advanced energy usage analysis, carbon footprint tracking, and sustainability reporting',
        metrics: 'Track energy usage down to individual zones',
        color: 'from-yellow-500 to-orange-400',
      },
      {
        icon: Building,
        title: 'Multi-Zone Management System',
        description: 'Centralized control of multiple buildings and zones with customizable climate profiles',
        metrics: 'Manage 500+ zones from single interface',
        color: 'from-purple-500 to-pink-400',
      },
      {
        icon: Settings,
        title: 'Enterprise Integration Capabilities',
        description: 'Seamless integration with BMS, ERP systems, and third-party facility management platforms',
        metrics: 'Connect with 200+ building systems',
        color: 'from-cyan-500 to-blue-400',
      },
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated reporting and continuous monitoring',
        metrics: '100% compliance audit success rate',
        color: 'from-green-500 to-emerald-400',
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis, security posture optimization, and comprehensive risk assessment',
        metrics: '360° security visibility dashboard',
        color: 'from-blue-500 to-cyan-400',
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited facilities, users, and concurrent connections',
        metrics: 'Scale to 10,000+ locations seamlessly',
        color: 'from-purple-500 to-violet-400',
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation, real-time attack prevention, and automated security responses',
        metrics: '99.99% attack mitigation success',
        color: 'from-orange-500 to-red-400',
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, smart card integration, and passwordless access',
        metrics: 'Support for 20+ authentication methods',
        color: 'from-emerald-500 to-teal-400',
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM capabilities, behavioral analytics, and automated incident response',
        metrics: 'Process 1M+ security events per second',
        color: 'from-indigo-500 to-blue-400',
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, secure backup systems, and comprehensive recovery solutions',
        metrics: '99.999% data recovery guarantee',
        color: 'from-violet-500 to-purple-400',
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation, anomaly detection, and continuous network monitoring',
        metrics: 'Monitor 100,000+ network endpoints',
        color: 'from-cyan-500 to-teal-400',
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, compliance validation, and security posture improvement',
        metrics: '24/7 automated security assessments',
        color: 'from-rose-500 to-pink-400',
      },
    ],
  },

  'remote-facility-monitoring': {
    keyBenefits: [
      {
        icon: BarChart3,
        title: 'Comprehensive Real-Time Monitoring Dashboard',
        description: 'Centralized command center with live facility status, performance metrics, and alert management',
        metrics: 'Monitor 1,000+ facilities in real-time',
        color: 'from-blue-500 to-cyan-400',
      },
      {
        icon: Building,
        title: 'Advanced Multi-Site Management Platform',
        description: 'Unified control across multiple locations with customizable dashboards and role-based access',
        metrics: 'Manage unlimited facilities from one interface',
        color: 'from-green-500 to-emerald-400',
      },
      {
        icon: AlertTriangle,
        title: 'Intelligent Alert & Notification System',
        description: 'Smart alert prioritization, automated escalation, and multi-channel notification delivery',
        metrics: '99.9% alert delivery reliability',
        color: 'from-orange-500 to-amber-400',
      },
      {
        icon: TrendingUp,
        title: 'Performance Tracking & Analytics',
        description: 'Advanced analytics for facility performance optimization, trend analysis, and predictive insights',
        metrics: 'Track 200+ performance indicators',
        color: 'from-purple-500 to-violet-400',
      },
      {
        icon: Zap,
        title: 'Energy Management Integration',
        description: 'Real-time energy monitoring, usage optimization, and cost management across all facilities',
        metrics: '30% average energy cost reduction',
        color: 'from-indigo-500 to-blue-400',
      },
      {
        icon: Smartphone,
        title: 'Mobile Access & Control Platform',
        description: 'Full-featured mobile application for remote monitoring, control, and emergency response',
        metrics: '24/7 mobile accessibility from anywhere',
        color: 'from-teal-500 to-cyan-400',
      },
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: 'AI-Powered Facility Analytics Engine',
        description: 'Machine learning for predictive maintenance, anomaly detection, and optimization recommendations',
        metrics: 'Analyze 50M+ data points daily',
        color: 'from-rose-500 to-pink-400',
      },
      {
        icon: Wrench,
        title: 'Predictive Maintenance Intelligence',
        description: 'Advanced algorithms for equipment failure prediction and optimal maintenance scheduling',
        metrics: 'Reduce maintenance costs by 45%',
        color: 'from-emerald-500 to-teal-400',
      },
      {
        icon: BarChart3,
        title: 'Automated Reporting & Insights',
        description: 'Intelligent report generation, performance benchmarking, and actionable insights delivery',
        metrics: 'Generate 500+ custom reports automatically',
        color: 'from-blue-500 to-indigo-400',
      },
      {
        icon: Database,
        title: 'Cloud Synchronization & Backup',
        description: 'Real-time data synchronization, secure cloud storage, and disaster recovery capabilities',
        metrics: '99.999% data availability guarantee',
        color: 'from-yellow-500 to-orange-400',
      },
      {
        icon: Settings,
        title: 'Custom Dashboard & Visualization',
        description: 'Personalized dashboards, advanced data visualization, and configurable monitoring views',
        metrics: 'Create unlimited custom dashboards',
        color: 'from-purple-500 to-pink-400',
      },
      {
        icon: Globe,
        title: 'Scalable Cloud Architecture',
        description: 'Elastic infrastructure supporting massive scale with automatic load balancing and redundancy',
        metrics: 'Scale to monitor 100,000+ facilities',
        color: 'from-cyan-500 to-blue-400',
      },
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated reporting and continuous monitoring',
        metrics: '100% compliance audit success rate',
        color: 'from-green-500 to-emerald-400',
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis, security posture optimization, and comprehensive risk assessment',
        metrics: '360° security visibility dashboard',
        color: 'from-blue-500 to-cyan-400',
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited facilities, users, and concurrent connections',
        metrics: 'Scale to 10,000+ locations seamlessly',
        color: 'from-purple-500 to-violet-400',
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation, real-time attack prevention, and automated security responses',
        metrics: '99.99% attack mitigation success',
        color: 'from-orange-500 to-red-400',
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, smart card integration, and passwordless access',
        metrics: 'Support for 20+ authentication methods',
        color: 'from-emerald-500 to-teal-400',
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM capabilities, behavioral analytics, and automated incident response',
        metrics: 'Process 1M+ security events per second',
        color: 'from-indigo-500 to-blue-400',
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, secure backup systems, and comprehensive recovery solutions',
        metrics: '99.999% data recovery guarantee',
        color: 'from-violet-500 to-purple-400',
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation, anomaly detection, and continuous network monitoring',
        metrics: 'Monitor 100,000+ network endpoints',
        color: 'from-cyan-500 to-teal-400',
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, compliance validation, and security posture improvement',
        metrics: '24/7 automated security assessments',
        color: 'from-rose-500 to-pink-400',
      },
    ],
  },

  'predictive-maintenance-platform': {
    keyBenefits: [
      {
        icon: Cpu,
        title: 'Machine Learning Failure Prediction',
        description: 'Advanced AI algorithms analyzing equipment data patterns to predict failures before they occur',
        metrics: 'Predict failures 45 days in advance',
        color: 'from-blue-500 to-cyan-400',
      },
      {
        icon: Activity,
        title: 'Asset Performance Monitoring',
        description: 'Continuous monitoring of equipment health, performance metrics, and operational efficiency',
        metrics: 'Monitor 10,000+ assets simultaneously',
        color: 'from-green-500 to-emerald-400',
      },
      {
        icon: Clock,
        title: 'Automated Maintenance Scheduling',
        description: 'Intelligent work order generation and technician scheduling based on predictive insights',
        metrics: '60% reduction in unplanned downtime',
        color: 'from-orange-500 to-amber-400',
      },
      {
        icon: AlertTriangle,
        title: 'Real-Time Equipment Alert System',
        description: 'Instant notifications for critical equipment issues with severity-based escalation workflows',
        metrics: '99.9% alert delivery within 30 seconds',
        color: 'from-purple-500 to-violet-400',
      },
      {
        icon: BarChart3,
        title: 'Comprehensive Reporting & Analytics',
        description: 'Detailed maintenance reports, cost analysis, and performance optimization insights',
        metrics: 'Generate 100+ automated reports',
        color: 'from-indigo-500 to-blue-400',
      },
      {
        icon: Smartphone,
        title: 'Mobile Technician Access',
        description: 'Field-ready mobile application with work orders, equipment history, and diagnostic tools',
        metrics: 'Increase technician productivity by 35%',
        color: 'from-teal-500 to-cyan-400',
      },
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: 'Advanced Machine Learning Algorithms',
        description: 'Proprietary ML models for equipment lifecycle prediction, optimization, and maintenance planning',
        metrics: 'Process 100M+ sensor readings daily',
        color: 'from-rose-500 to-pink-400',
      },
      {
        icon: Activity,
        title: 'IoT Sensor Integration Platform',
        description: 'Seamless integration with industrial IoT sensors, SCADA systems, and equipment controllers',
        metrics: 'Connect 50,000+ IoT sensors',
        color: 'from-emerald-500 to-teal-400',
      },
      {
        icon: TrendingUp,
        title: 'Predictive Analytics Engine',
        description: 'Advanced analytics for maintenance optimization, parts inventory planning, and cost forecasting',
        metrics: 'Optimize maintenance costs by 40%',
        color: 'from-blue-500 to-indigo-400',
      },
      {
        icon: Wrench,
        title: 'Automated Workflow Management',
        description: 'Intelligent workflow automation for maintenance processes, approvals, and resource allocation',
        metrics: 'Automate 90% of routine workflows',
        color: 'from-yellow-500 to-orange-400',
      },
      {
        icon: Settings,
        title: 'Enterprise System Integration',
        description: 'Native integration with ERP, CMMS, and facility management systems for seamless data flow',
        metrics: 'Integrate with 100+ enterprise systems',
        color: 'from-purple-500 to-pink-400',
      },
      {
        icon: Database,
        title: 'Scalable Cloud Platform',
        description: 'Enterprise-grade cloud infrastructure with automatic scaling and high-availability architecture',
        metrics: 'Scale to support 1M+ assets',
        color: 'from-cyan-500 to-blue-400',
      },
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated reporting and continuous monitoring',
        metrics: '100% compliance audit success rate',
        color: 'from-green-500 to-emerald-400',
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis, security posture optimization, and comprehensive risk assessment',
        metrics: '360° security visibility dashboard',
        color: 'from-blue-500 to-cyan-400',
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited facilities, users, and concurrent connections',
        metrics: 'Scale to 10,000+ locations seamlessly',
        color: 'from-purple-500 to-violet-400',
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation, real-time attack prevention, and automated security responses',
        metrics: '99.99% attack mitigation success',
        color: 'from-orange-500 to-red-400',
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, smart card integration, and passwordless access',
        metrics: 'Support for 20+ authentication methods',
        color: 'from-emerald-500 to-teal-400',
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM capabilities, behavioral analytics, and automated incident response',
        metrics: 'Process 1M+ security events per second',
        color: 'from-indigo-500 to-blue-400',
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, secure backup systems, and comprehensive recovery solutions',
        metrics: '99.999% data recovery guarantee',
        color: 'from-violet-500 to-purple-400',
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation, anomaly detection, and continuous network monitoring',
        metrics: 'Monitor 100,000+ network endpoints',
        color: 'from-cyan-500 to-teal-400',
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, compliance validation, and security posture improvement',
        metrics: '24/7 automated security assessments',
        color: 'from-rose-500 to-pink-400',
      },
    ],
  },

  'energy-management-system': {
    keyBenefits: [
      {
        icon: Battery,
        title: 'Real-Time Energy Monitoring & Control',
        description: 'Comprehensive energy usage tracking with intelligent load management and demand response capabilities',
        metrics: 'Monitor energy usage down to individual circuits',
        color: 'from-blue-500 to-cyan-400',
      },
      {
        icon: TrendingUp,
        title: 'AI-Powered Optimization Algorithms',
        description: 'Machine learning algorithms for energy efficiency optimization, peak demand reduction, and cost minimization',
        metrics: '40% reduction in energy costs',
        color: 'from-green-500 to-emerald-400',
      },
      {
        icon: BarChart3,
        title: 'Sustainability Reporting & Analytics',
        description: 'Comprehensive carbon footprint tracking, ESG reporting, and sustainability goal monitoring',
        metrics: 'Track carbon emissions with 99% accuracy',
        color: 'from-orange-500 to-amber-400',
      },
      {
        icon: Zap,
        title: 'Automated Demand Response System',
        description: 'Smart grid integration with automated load shedding, peak shaving, and energy storage optimization',
        metrics: 'Participate in 50+ demand response programs',
        color: 'from-purple-500 to-violet-400',
      },
      {
        icon: Settings,
        title: 'Renewable Energy Integration',
        description: 'Solar, wind, and battery storage integration with intelligent energy source switching and optimization',
        metrics: 'Manage 100MW+ renewable capacity',
        color: 'from-indigo-500 to-blue-400',
      },
      {
        icon: Activity,
        title: 'Cost Tracking & Budget Analytics',
        description: 'Detailed energy cost analysis, budget forecasting, and ROI tracking for efficiency investments',
        metrics: 'Track energy spend across 1,000+ accounts',
        color: 'from-teal-500 to-cyan-400',
      },
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: 'AI-Powered Energy Optimization Engine',
        description: 'Advanced machine learning for predictive energy management, load forecasting, and efficiency optimization',
        metrics: 'Process 1B+ energy data points daily',
        color: 'from-rose-500 to-pink-400',
      },
      {
        icon: Globe,
        title: 'Smart Grid Integration Platform',
        description: 'Seamless integration with utility smart grid systems, energy markets, and grid services',
        metrics: 'Connect to 500+ utility programs',
        color: 'from-emerald-500 to-teal-400',
      },
      {
        icon: BarChart3,
        title: 'Carbon Footprint Analytics',
        description: 'Advanced carbon accounting, emissions tracking, and sustainability performance monitoring',
        metrics: 'Calculate carbon footprint in real-time',
        color: 'from-blue-500 to-indigo-400',
      },
      {
        icon: Settings,
        title: 'Automated Control Systems',
        description: 'Intelligent building automation integration with HVAC, lighting, and equipment control systems',
        metrics: 'Control 10,000+ connected devices',
        color: 'from-yellow-500 to-orange-400',
      },
      {
        icon: CheckCircle,
        title: 'Compliance Management Suite',
        description: 'Automated regulatory reporting, energy audit support, and compliance monitoring tools',
        metrics: 'Support 100+ regulatory frameworks',
        color: 'from-purple-500 to-pink-400',
      },
      {
        icon: Smartphone,
        title: 'Mobile Energy Management',
        description: 'Full-featured mobile app for remote monitoring, control, and energy management on-the-go',
        metrics: '24/7 energy control from mobile device',
        color: 'from-cyan-500 to-blue-400',
      },
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with automated reporting and continuous monitoring',
        metrics: '100% compliance audit success rate',
        color: 'from-green-500 to-emerald-400',
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat analysis, security posture optimization, and comprehensive risk assessment',
        metrics: '360° security visibility dashboard',
        color: 'from-blue-500 to-cyan-400',
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade platform supporting unlimited facilities, users, and concurrent connections',
        metrics: 'Scale to 10,000+ locations seamlessly',
        color: 'from-purple-500 to-violet-400',
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'AI-powered threat mitigation, real-time attack prevention, and automated security responses',
        metrics: '99.99% attack mitigation success',
        color: 'from-orange-500 to-red-400',
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO, biometric authentication, smart card integration, and passwordless access',
        metrics: 'Support for 20+ authentication methods',
        color: 'from-emerald-500 to-teal-400',
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM capabilities, behavioral analytics, and automated incident response',
        metrics: 'Process 1M+ security events per second',
        color: 'from-indigo-500 to-blue-400',
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, secure backup systems, and comprehensive recovery solutions',
        metrics: '99.999% data recovery guarantee',
        color: 'from-violet-500 to-purple-400',
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced micro-segmentation, anomaly detection, and continuous network monitoring',
        metrics: 'Monitor 100,000+ network endpoints',
        color: 'from-cyan-500 to-teal-400',
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, compliance validation, and security posture improvement',
        metrics: '24/7 automated security assessments',
        color: 'from-rose-500 to-pink-400',
      },
    ],
  },
};